import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { fromPromise } from "mobx-utils";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { parseOrReturnError } from "../../services/utils";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import { SurveillancePolicy } from "@whatslly/types/surveillance/SurveillancePolicy";
import { modalService } from "@whatslly/ui-components/modals/ModalService";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import { TFunction } from "i18next";
import { DLPRule } from "@whatslly/types/dlp/DLPRule";
import { DLPRuleModal } from "../Dlp/DLPRuleModal";
import type { ISurveillanceStore } from "./ISurveillanceStore";
import { MessageModal } from "@whatslly/ui-components/modals/MessageModal";
import { GetDlpRulesResponse } from "@whatslly/types/integrations/DlpRulesRequest";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import { DestinationsStore } from "../Destinations/DestinationsStore";
import { UrlStore } from "../../services/urlStore";

@injectable()
export class SurveillanceStore implements ISurveillanceStore {
  private forceUpdate = 0;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(DestinationsStore) private readonly destinationsStore: DestinationsStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(UrlStore) private readonly urlStore: UrlStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<SurveillanceStore, "forceUpdate">(this, {
      forceUpdate: observable,
      policies: computed,
      rules: computed,
      allowDlpAI: computed,
      isSidecarEnabled: computed,
      createPolicy: action.bound,
      createRule: action.bound,
    });
  }

  get policies() {
    this.forceUpdate;
    return fromPromise(this.getSurveillancePolicies());
  }

  get rules() {
    this.forceUpdate;
    return fromPromise(this.getSurveillanceRules());
  }

  get allowDlpAI() {
    this.forceUpdate;
    return this.orgConfigsStore.getObservableConfig("allow_dlp_ai");
  }

  get isSidecarEnabled() {
    this.forceUpdate;
    return this.orgConfigsStore.getObservableConfig("sidecars_enabled");
  }

  private async getSurveillanceRules() {
    const res = await getJSON({
      urlSuffix: `/integrations/surveillance/rules?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<GetDlpRulesResponse>(r, this.logger));
    return res.success ? res.rules : [];
  }

  private async getSurveillancePolicies() {
    const res = await getJSON({
      urlSuffix: `/integrations/surveillance/policies?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
    }).then((r) => parseOrReturnError<{ success: true; policies: SurveillancePolicy[] }>(r, this.logger));
    return res.success ? res.policies : [];
  }

  private reload() {
    runInAction(() => this.forceUpdate++);
  }

  private getPolicyBuilderUrl(policyId?: string) {
    const search = this.urlStore.searchParams?.toString();
    const basePath = policyId ? `/surveillance/policies/${policyId}` : "/surveillance/policies/new";
    return search ? `${basePath}?${search}` : basePath;
  }

  async getPolicyById(policyId: string) {
    const policies = await this.getSurveillancePolicies();
    return policies.find((policy) => policy.id === policyId);
  }

  async savePolicy(policy: SurveillancePolicy, isEditMode = false) {
    if (isEditMode) {
      await this.updatePolicy(policy);
      return;
    }

    await postJSON({
      urlSuffix: `/integrations/surveillance/policies?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ policy }),
    });
    this.reload();
  }

  async createPolicy() {
    this.urlStore.navigate(this.getPolicyBuilderUrl());
  }

  async deletePolicy(t: TFunction, policy?: SurveillancePolicy) {
    if (!policy) return;
    const res = await modalService.openModal(ConfirmModal, {
      title: t("surveillance_page.delete_policy"),
      body: t("surveillance_page.delete_policy_modal_message"),
    }).promise;
    if (!res?.result) return;
    await deleteJSON({
      urlSuffix: `/integrations/surveillance/policies?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ policyIds: [policy.id] }),
    });
    this.reload();
  }

  private async updatePolicy(policy: SurveillancePolicy) {
    await putJSON({
      urlSuffix: `/integrations/surveillance/policies/${policy.id}?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ policy }),
    });
    this.reload();
  }

  async editPolicy(policy?: SurveillancePolicy) {
    if (!policy) return;
    this.urlStore.navigate(this.getPolicyBuilderUrl(policy.id));
  }

  async togglePolicyStatus(policy: SurveillancePolicy) {
    await this.updatePolicy({ ...policy, enabled: !policy.enabled });
  }

  async deleteRule(t: TFunction, rule?: DLPRule) {
    if (!rule) return;
    const policies = await this.policies;
    const used = policies.filter((p) =>
      p.conditions.some((c) => {
        if (c.type !== "rule") return false;
        return Array.isArray(c.id) ? c.id.includes(rule.id) : c.id === rule.id;
      })
    );
    if (used.length > 0) {
      await modalService.openModal(MessageModal, {
        title: t("surveillance_page.rule_in_use"),
        children: t("surveillance_page.rule_in_use_description", { policies: used.map((p) => p.name).join(", ") }),
        okTestId: "ok_button",
      }).promise;
      return;
    }
    const res = await modalService.openModal(ConfirmModal, {
      title: t("surveillance_page.delete_rule"),
      body: t("surveillance_page.delete_rule_modal_message"),
    }).promise;
    if (!res?.result) return;
    await deleteJSON({
      urlSuffix: `/integrations/surveillance/rules?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ ruleIds: [rule.id] }),
    });
    this.reload();
  }

  async createRule() {
    const allowDlpAI = this.allowDlpAI.state === "fulfilled" ? this.allowDlpAI.value : false;
    const rule = await modalService.openModal(DLPRuleModal, { allowDlpAI }).promise;
    if (!rule) return;
    await postJSON({
      urlSuffix: `/integrations/surveillance/rules?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ rule }),
    });
    this.reload();
  }

  async editRule(rule?: DLPRule) {
    if (!rule) return;
    const allowDlpAI = this.allowDlpAI.state === "fulfilled" ? this.allowDlpAI.value : false;
    const newRule = await modalService.openModal(DLPRuleModal, { rule, allowDlpAI }).promise;
    if (!newRule) return;
    await putJSON({
      urlSuffix: `/integrations/surveillance/rules/${rule.id}?orgId=${await this.orgStore.orgId}`,
      etoken: this.userStore.etoken ?? "",
      pod: await this.orgStore.wPod,
      body: JSON.stringify({ rule: newRule }),
    });
    this.reload();
  }
}
